import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useWxWork } from '@cloud/wxwork-workbench';
import PageLayout from '@/layouts/page';
import List, { ActionType as ListActionType } from '@/components/List';
import Button from '@/components/Button';
import { DataSource, defaultAvatarUrls } from '@/constants';
import { finishTodoEvent, queryReminderList, queryReminderStatus } from './service';
import styles from './index.less';
import Toast from '@/components/Toast';
import { useHistory, useParams } from 'react-router-dom';
import { goReplace } from '@/utils';


export enum EventStatus {
  pending = 'NOT_DEAL',
  finished = 'FINISH'
}

type StatusAnalysis = Record<EventStatus, number>
interface Analysis {
  total: number;
  status: StatusAnalysis;
}

const defaultAnalysis: Analysis = {
  total: 0,
  status: {
    [EventStatus.pending]: 0,
    [EventStatus.finished]: 0
  }
};

const tabs = [
  { key: EventStatus.pending, title: '待处理' },
  { key: EventStatus.finished, title: '已处理' }
];

const jumpOnGotId = async (
  history: ReturnType<typeof useHistory>,
  id: string,
  signature: ReturnType<typeof useWxWork>,
  callback: () => void
) => {
  const parent = history.location.pathname.split('/').filter(x => x !== id).join('/');
  try {
    const trueStatus = await queryReminderStatus(id, signature);
    goReplace(history, `${parent}/${trueStatus}`);
  } catch {
    goReplace(history, `${parent}/${EventStatus.pending}`);
  } finally {
    callback();
  }
}

function showDynamicContent (text: string) {
  try {
    const { desc } = JSON.parse(text) || {};
    return desc || text;
  } catch {
    return text;
  }
}

const TodoReminderPage: FC<{}> = () => {
  const signature = useWxWork();
  const history = useHistory();
  const { status } = useParams<{ status?: string }>();
  const listActionRef = useRef<ListActionType>();
  const [initialized, init] = useState<boolean>(false);
  const [analysis, setAnalysis] = useState<Analysis>(defaultAnalysis);

  useEffect(() => {
    if (initialized && listActionRef.current) {
      listActionRef.current.refresh();
    }
  }, [initialized]);
  
  useEffect(() => {
    if (!status) {
      const parent = history.location.pathname.split('/').join('/');
      goReplace(history, `${parent}/${EventStatus.pending}`);
      return;
    }
    for (let k in EventStatus) {
      if (EventStatus[k] === status) {
        if (listActionRef.current) {
          listActionRef.current.refresh();
        }
        init(true);
        return;
      }
    }
    jumpOnGotId(history, status, signature, () => init(true));
  }, [status, history, signature]);

  const handleTabChange = useCallback((k: EventStatus) => {
    const parent = history.location.pathname.split('/').filter(x => x !== status).join('/');
    goReplace(history, `${parent}/${k}`);
  }, [status, history]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canOpenExtUserCard = useMemo(() => 'invoke' in wx, [signature]);

  const handleOpenExtUserCard = useCallback((extUserId: string) => {
    wx?.invoke('openUserProfile', { 'type': 2, 'userid': extUserId }, (res: any) => {
      if (res?.err_msg !== 'openUserProfile:ok') {
        Toast.error('未知错误，无法打开客户名片');
        console.log('[打开名片错误]：', res?.err_msg);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const handleJump2Chat = useCallback((chatId: string) => {
    wx.openEnterpriseChat({
      chatId,
      fail: res => {
        if (res.errMsg !== 'openEnterpriseChat:ok') {
          Toast.error('无法跳转到群聊，请手动打开');
        }
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const handleFinishEvent = useCallback(async (id: UKey) => {
    await finishTodoEvent(id, signature);
    listActionRef.current.refresh();
  }, [signature]);

  const fetchList = useCallback(async pagination => {
    const res = await queryReminderList({ status, auth: signature, ...pagination });
    setAnalysis({ ...res.analysis });
    return { data: res.data, pagination: res.pagination };
  }, [status, signature]);

  const renderItem = useCallback(({
    id, extUserId, pendingStatus,
    dynamicContent, pendingOverdue, dynamicTime,
    nickName, avatar,
    dataSource, chatId
  }) => (
    <div className={classNames(styles.item, styles[pendingStatus])}>
      <h4 className={styles.title}>{showDynamicContent(dynamicContent)}</h4>
      <div className={styles.time}>
        <span>{dynamicTime}</span>
        {pendingOverdue && <span className={styles.overtime}>{pendingOverdue}</span>}
      </div>
      <div className={styles.footer}>
        <div className={styles.userInfo}>
          <span className={styles.avatar} style={{ backgroundImage: `url(${avatar || defaultAvatarUrls[dataSource || DataSource.CUSTOMER]})` }} />
          <span className={styles.name}>{nickName || (dataSource === DataSource.CHAT ? '未命名群' : '')}</span>
        </div>
        <div className={styles.actions}>
          <Button
            disabled={!canOpenExtUserCard}
            onClick={dataSource === DataSource.CHAT ? () => handleJump2Chat(chatId) : () => handleOpenExtUserCard(extUserId)}
          >跟进</Button>
          {(pendingStatus === tabs[1].key) ? null :
            <Button onClick={() => handleFinishEvent(id)}>完成</Button>}
        </div>
      </div>
    </div>
  ), [canOpenExtUserCard, handleFinishEvent, handleOpenExtUserCard, handleJump2Chat]);

  if (!initialized) {
    return (
      <div className={styles.loading}>
        <span className={styles.spin} />
      </div>
    );
  }

  return (
    <PageLayout
      tabs={tabs.map(({ key, title }) => ({
        key, title,
        count: analysis.status[key]
      }))}
      activeKey={status}
      onTabChange={handleTabChange}
    >
      <div className={styles.main}>
        <List
          actionRef={r => listActionRef.current = r}
          request={fetchList}
          loadDataLength={50}
          scrollThreshold='0px'
          rowKey='id'
          renderItem={renderItem}
        />
      </div>
    </PageLayout>
  );
}

export default TodoReminderPage;